import * as React from "react";
import { useTranslation } from "react-i18next";
import { Check, Copy, ExternalLink, Globe2, Loader2, RefreshCw, Star, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { fetchWithCsrf } from "./fetchWithCsrf";
import { resolveApiUrl } from "./resolveApiUrl";

type DomainStatus = "pending_dns" | "issuing_certificate" | "live" | "error";

type ProjectDomain = {
  id: string;
  hostname: string;
  url: string;
  status: DomainStatus;
  isPrimary: boolean;
  deploymentId: string | null;
  ownershipVerified: boolean;
  error: string | null;
  records: {
    ownership: { type: "TXT"; name: string; value: string };
    routing: { type: "CNAME"; name: string; value: string };
  };
};

type Toast = (message: string, variant: "success" | "error" | "warning") => void;

const readError = async (response: Response, fallback: string): Promise<string> => {
  const body = (await response.json().catch(() => ({}))) as { error?: unknown };
  return typeof body.error === "string" ? body.error : fallback;
};

const statusClass: Record<DomainStatus, string> = {
  pending_dns: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  issuing_certificate: "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300",
  live: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  error: "border-destructive/30 bg-destructive/10 text-destructive"
};

export const ProjectDomainsPanel = ({
  projectId,
  onToast
}: {
  projectId: string;
  onToast: Toast;
}): React.ReactElement => {
  const { t } = useTranslation();
  const [domains, setDomains] = React.useState<ProjectDomain[]>([]);
  const [hostname, setHostname] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [busyId, setBusyId] = React.useState<string | null>(null);
  const [adding, setAdding] = React.useState(false);
  const [copied, setCopied] = React.useState<string | null>(null);

  const reload = React.useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await fetch(resolveApiUrl(`/api/projects/${projectId}/domains`), {
        credentials: "include"
      });
      if (!response.ok) throw new Error(await readError(response, t("projectSettings.domains.loadFailed")));
      setDomains((await response.json()) as ProjectDomain[]);
    } catch (error) {
      onToast(error instanceof Error ? error.message : t("projectSettings.domains.loadFailed"), "error");
    } finally {
      setLoading(false);
    }
  }, [onToast, projectId, t]);

  React.useEffect(() => {
    void reload();
  }, [reload]);

  const addDomain = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    const value = hostname.trim();
    if (!value) return;
    setAdding(true);
    try {
      const response = await fetchWithCsrf(`/api/projects/${projectId}/domains`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ hostname: value })
      });
      if (!response.ok) throw new Error(await readError(response, t("projectSettings.domains.addFailed")));
      setHostname("");
      await reload();
      onToast(t("projectSettings.domains.added"), "success");
    } catch (error) {
      onToast(error instanceof Error ? error.message : t("projectSettings.domains.addFailed"), "error");
    } finally {
      setAdding(false);
    }
  };

  const mutate = async (domain: ProjectDomain, action: "verify" | "primary" | "remove"): Promise<void> => {
    if (action === "remove" && !window.confirm(t("projectSettings.domains.removeConfirm", { hostname: domain.hostname }))) {
      return;
    }
    setBusyId(domain.id);
    try {
      const path = `/api/projects/${projectId}/domains/${domain.id}${action === "verify" ? "/verify" : ""}`;
      const response = await fetchWithCsrf(path, {
        method: action === "remove" ? "DELETE" : action === "primary" ? "PATCH" : "POST",
        ...(action === "primary"
          ? { headers: { "Content-Type": "application/json" }, body: JSON.stringify({ isPrimary: true }) }
          : {})
      });
      if (!response.ok) throw new Error(await readError(response, t("projectSettings.domains.actionFailed")));
      await reload();
      onToast(t(`projectSettings.domains.${action}Success`), "success");
    } catch (error) {
      onToast(error instanceof Error ? error.message : t("projectSettings.domains.actionFailed"), "error");
    } finally {
      setBusyId(null);
    }
  };

  const copyRecord = async (key: string, value: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      window.setTimeout(() => setCopied((current) => (current === key ? null : current)), 1200);
    } catch {
      onToast(t("projectSettings.domains.copyFailed"), "warning");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">{t("projectSettings.domains.heading")}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t("projectSettings.domains.intro")}</p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t("projectSettings.domains.addTitle")}</CardTitle>
          <CardDescription>{t("projectSettings.domains.addDescription")}</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="flex flex-col gap-3 sm:flex-row sm:items-end" onSubmit={(event) => void addDomain(event)}>
            <div className="min-w-0 flex-1 space-y-1.5">
              <Label htmlFor="custom-domain-hostname">{t("projectSettings.domains.hostname")}</Label>
              <Input
                id="custom-domain-hostname"
                value={hostname}
                onChange={(event) => setHostname(event.target.value)}
                placeholder="app.example.com"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
              />
            </div>
            <Button type="submit" disabled={adding || !hostname.trim()}>
              {adding ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <Globe2 className="size-4" aria-hidden />}
              {t("projectSettings.domains.add")}
            </Button>
          </form>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground" role="status">
          <Loader2 className="size-4 animate-spin" aria-hidden />
          {t("projectSettings.domains.loading")}
        </div>
      ) : domains.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          {t("projectSettings.domains.empty")}
        </div>
      ) : (
        <div className="space-y-4">
          {domains.map((domain) => {
            const busy = busyId === domain.id;
            return (
              <Card key={domain.id}>
                <CardHeader className="pb-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <CardTitle className="flex flex-wrap items-center gap-2 text-base">
                        <span className="truncate">{domain.hostname}</span>
                        {domain.isPrimary ? (
                          <span className="inline-flex items-center gap-1 rounded-full border bg-muted px-2 py-0.5 text-[11px] font-medium">
                            <Star className="size-3" aria-hidden /> {t("projectSettings.domains.primary")}
                          </span>
                        ) : null}
                      </CardTitle>
                      <CardDescription className="mt-1">
                        {domain.deploymentId
                          ? t("projectSettings.domains.boundToProduction")
                          : t("projectSettings.domains.noProductionDeployment")}
                      </CardDescription>
                    </div>
                    <span className={cn("rounded-full border px-2.5 py-1 text-xs font-medium", statusClass[domain.status])}>
                      {t(`projectSettings.domains.status.${domain.status}`)}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {domain.status !== "live" ? (
                    <div className="space-y-3 rounded-lg border bg-muted/20 p-3">
                      <p className="text-sm text-muted-foreground">{t("projectSettings.domains.dnsIntro")}</p>
                      {[domain.records.ownership, domain.records.routing].map((record) => {
                        const recordKey = `${domain.id}:${record.type}`;
                        return (
                          <div key={record.type} className="grid gap-2 sm:grid-cols-[4rem_minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-center">
                            <span className="text-xs font-semibold text-muted-foreground">{record.type}</span>
                            <code className="truncate rounded bg-background px-2 py-1.5 text-xs" title={record.name}>{record.name}</code>
                            <code className="truncate rounded bg-background px-2 py-1.5 text-xs" title={record.value}>{record.value}</code>
                            <Button type="button" size="icon" variant="ghost" onClick={() => void copyRecord(recordKey, `${record.name} ${record.type} ${record.value}`)}>
                              {copied === recordKey ? <Check className="size-4" aria-hidden /> : <Copy className="size-4" aria-hidden />}
                              <span className="sr-only">{t("projectSettings.domains.copyRecord")}</span>
                            </Button>
                          </div>
                        );
                      })}
                      {domain.error ? <p className="text-sm text-destructive">{domain.error}</p> : null}
                    </div>
                  ) : null}

                  <div className="flex flex-wrap gap-2">
                    {domain.ownershipVerified ? (
                      <Button asChild size="sm" variant="outline">
                        <a href={domain.url} target="_blank" rel="noreferrer">
                          <ExternalLink className="size-4" aria-hidden /> {t("projectSettings.domains.open")}
                        </a>
                      </Button>
                    ) : null}
                    {domain.status !== "live" ? (
                      <Button type="button" size="sm" disabled={busy} onClick={() => void mutate(domain, "verify")}>
                        <RefreshCw className={cn("size-4", busy && "animate-spin")} aria-hidden />
                        {t("projectSettings.domains.verify")}
                      </Button>
                    ) : null}
                    {!domain.isPrimary ? (
                      <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => void mutate(domain, "primary")}>
                        <Star className="size-4" aria-hidden /> {t("projectSettings.domains.setPrimary")}
                      </Button>
                    ) : null}
                    <Button type="button" size="sm" variant="ghost" disabled={busy} className="text-destructive hover:text-destructive" onClick={() => void mutate(domain, "remove")}>
                      <Trash2 className="size-4" aria-hidden /> {t("projectSettings.domains.remove")}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
